import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { AgentThreadRecord, isActiveThreadStatus } from "../AgentThreadRecord";

/**
 * Canonical TanStack query key for a single agent thread. Exported so seeders
 * (e.g. `useInvokeSkill` after starting a new thread) and listeners use the
 * same shape — without it, a seeder hard-coding `[..., true]` is invisible to
 * a consumer that mounted with `loadMessages: false`.
 */
export function activeAgentThreadQueryKey(
  threadId: string | undefined,
  loadMessages: boolean,
) {
  return ["activeAgentThread", threadId, loadMessages] as const;
}

/**
 * Partial-match prefix that targets every loadMessages variant for a thread.
 * Use with `cancelQueries` / `getQueriesData` / `invalidateQueries` when a
 * mutation needs to act on the thread record regardless of which loadMessages
 * the live consumer mounted with.
 */
export function activeAgentThreadQueryKeyPrefix(threadId: string | undefined) {
  return ["activeAgentThread", threadId] as const;
}

interface UseActiveAgentThreadOptions {
  /**
   * Organization ID for the request
   */
  orgId: string;

  /**
   * Whether to load messages with the thread record
   * @default true
   */
  loadMessages?: boolean;

  /**
   * Polling interval in milliseconds when assistant is generating
   * @default 1000
   */
  pollingIntervalMs?: number;
}

/**
 * Hook to fetch a thread by ID with automatic polling.
 *
 * Polls the thread endpoint every 1 second (configurable) until the most recent
 * assistant message is completed. Stops polling when:
 * - The last message has role === AgentThreadRole.ASSISTANT
 * - AND status === AgentThreadMessageStatus.COMPLETED
 *
 * @param threadId - The ID of the thread to fetch
 * @param options - Configuration options
 * @returns React Query result with thread data
 */
export function useActiveAgentThread(
  threadId: string | undefined,
  options: UseActiveAgentThreadOptions,
): UseQueryResult<AgentThreadRecord, Error> {
  const { agentThreadService } = useDomainServices();
  const queryClient = useQueryClient();
  const { orgId, loadMessages = true, pollingIntervalMs = 500 } = options;

  return useQuery<AgentThreadRecord, Error>({
    queryKey: activeAgentThreadQueryKey(threadId, loadMessages),
    queryFn: async ({ signal, queryKey }) => {
      if (threadId === undefined) {
        return Promise.reject(new Error("Invalid threadId"));
      }

      // Access previous data from React Query's cache
      const previousData =
        queryClient.getQueryData<AgentThreadRecord>(queryKey);

      if (previousData?.continuation_token) {
        // Use delta update if we have previous data with continuation token
        return await agentThreadService.updateAgentThreadFromDeltas(
          previousData,
          {
            orgId,
            abortSignal: signal,
          },
        );
      }

      // First fetch or no continuation token - get full data
      return await agentThreadService.getAgentThreadById(
        threadId,
        loadMessages,
        {
          orgId,
          abortSignal: signal,
        },
      );
    },
    enabled: threadId !== undefined && orgId !== undefined,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: (query) => {
      // Poll only while the backend still owes work. Gating on
      // `isActiveThreadStatus` (rather than `!== USER_TURN`) stops the poll on
      // every idle or terminal state, including the permanent `GOALS_FAILED`
      // and `NOT_STARTED`, which would otherwise refetch forever.
      return isActiveThreadStatus(query.state.data?.status)
        ? pollingIntervalMs
        : false;
    },
  });
}
